
import { ValidationObserver } from 'vee-validate'
import { Component, Mixins, Prop } from 'vue-property-decorator'
import { AddReviewStateMachineInterpreter } from '~/apps/post-review/machine'
import { ReviewFormData } from '~/apps/post-review/types'
import ValidationProviderMixin from '~/mixins/ValidationProviderMixin'
import { ReviewStarRatingValidator } from '../validation/ReviewStarRatingValidator'

@Component({ components: { ValidationObserver } })
export default class AddCampsiteReviewForm extends Mixins(
  ValidationProviderMixin,
) {
  @Prop()
    campsiteName: string

  @Prop()
    campsiteSlug: string

  @Prop()
    token: string

  @Prop()
    booking: AddReviewStateMachineInterpreter['state']['context']['booking']

  @Prop()
    errors: Record<string, string>

  @Prop()
    states: string[]

  anonymous = false
  disliked = ''
  liked = ''
  overallRate = 0
  rateAccomodation = 0
  rateChildFriendly = 0
  rateClean = 0
  rateFacilities = 0
  rateLocation = 0
  rateService = 0
  rateValue = 0
  rateWifi = 0
  recommendToAFriend: 'yes' | 'no' | null = null
  stayedIn = ''
  summary = ''
  travellingAs = ''
  willYouReturn: 'yes' | 'no' | null = null
  photos: string[] = []

  photosValid = false

  defaultSelectName = this.$t('Please choose...')

  overallRateErrors: string[] = []

  shouldShowThemesOption = false

  async validateOveralRate() {
    const validator = new ReviewStarRatingValidator(
      this.overallRate,
      'starRate',
    )
    this.overallRateErrors = []
    try {
      await validator.validate('overallRateSelected')
    } catch {
      this.overallRateErrors = validator.errors
    }
  }

  hasSubmitError: boolean = false
  isSubmitting = false

  submit(validate) {
    this.isSubmitting = true
    this.hasSubmitError = false
    this.doSubmit(validate).catch((error) => {
      this.hasSubmitError = true
      this.isSubmitting = false
      const err = error as Error
      err.name = 'add-campsite-review-form.submit error'
      throw err
    })
  }

  private async doSubmit(validate) {
    await this.validateOveralRate()
    const valid = await validate()
    if (!valid || this.overallRateErrors.length) {
      const errorElement = document.querySelector('.error:first-of-type')
      if (!errorElement) {
        console.error(
          'No error element found',
          `valid=${valid}`,
          JSON.stringify(this.overallRateErrors),
          `photosValid=${this.photosValid}`,
        )
      }
      this.$puScrollTo('.error:first-of-type', {
        duration: 300,
        offset: -20,
      })
      this.isSubmitting = false
    } else {
      this.$emit('submit', this.formData)
      setTimeout(() => {
        if (document.querySelector('.error')) {
          this.$puScrollTo('.error', {
            duration: 300,
            offset: -20,
          })
        }
      }, 1000)
    }
  }

  // visitDate comes from booking.arrive
  private get formData(): Omit<ReviewFormData, 'visitDate'> {
    // We do * 2 it to make sure they reach backend as even values (as that's what backend expects and it matches what desktop currently does)
    return {
      anonymous: this.anonymous,
      disliked: this.disliked,
      liked: this.liked,
      overallRate: this.overallRate * 2,
      rateAccomodation: this.rateAccomodation * 2,
      rateChildFriendly: this.rateChildFriendly * 2,
      rateClean: this.rateClean * 2,
      rateFacilities: this.rateFacilities * 2,
      rateLocation: this.rateLocation * 2,
      rateService: this.rateService * 2,
      rateValue: this.rateValue * 2,
      rateWifi: this.rateWifi * 2,
      recommendToAFriend: this.recommendToAFriend === 'yes',
      stayedIn: this.selectedStayedInOptions,
      summary: this.summary,
      travellingAs: this.travellingAs,
      willYouReturn: this.willYouReturn === 'yes',
      theme: this.themesOptions
        .filter((o) => o.selected === true)
        .map((t) => t.value),
    }
  }

  openThemesOption() {
    this.shouldShowThemesOption = true
  }

  get selectedStayedInOptions() {
    return this.stayedInOptions.length === 1
      ? this.stayedInOptions[0].value
      : this.stayedIn
  }

  closeThemesOption() {
    this.shouldShowThemesOption = false
  }

  get stayedInOptions() {
    const categories = this.booking?.categories || undefined
    if (categories) {
      return this.stayedInAllOptions.filter((o) => categories.includes(o.value))
    }
    return this.stayedInAllOptions
  }

  travellingAsOptions = [
    { name: 'Family with young children', value: '1', selected: false },
    { name: 'Family with older children', value: '2', selected: false },
    { name: 'Mature couple', value: '3', selected: false },
    { name: 'Middle-aged couple', value: '11', selected: false },
    { name: 'Young couple', value: '4', selected: false },
    { name: 'Solo traveller-male', value: '5', selected: false },
    { name: 'Solo traveller-female', value: '6', selected: false },
    { name: 'With friends', value: '7', selected: false },
    { name: 'Extended family', value: '8', selected: false },
    { name: 'Group-mostly male', value: '9', selected: false },
    { name: 'Group-mostly female', value: '10', selected: false },
  ]

  stayedInAllOptions = [
    { name: 'Tent pitches', value: '4', selected: false },
    { name: 'Touring pitches', value: '3', selected: false },
    { name: 'Motorhome pitches', value: '10', selected: false },
    { name: 'Lodges, cabins, pods or huts', value: '7', selected: false },
    { name: 'Caravans for hire', value: '6', selected: false },
    { name: 'Rent a tent', value: '13', selected: false },
  ]

  themesOptions = [
    { name: 'Forest', value: '104', selected: false },
    { name: 'Budget/backpacker', value: '119', selected: false },
    { name: 'Luxury', value: '118', selected: false },
    { name: 'Wild camping', value: '45', selected: false },
    { name: 'Lake and/or mountain', value: '78', selected: false },
    { name: 'Remote location', value: '79', selected: false },
    { name: 'Walkers paradise', value: '84', selected: false },
    { name: 'Peaceful', value: '81', selected: false },
    { name: 'Wildlife haven', value: '80', selected: false },
    { name: 'Spectacular scenery', value: '77', selected: false },
    { name: 'Leave the car at home', value: '82', selected: false },
    { name: 'Gastronomic delight', value: '85', selected: false },
  ]
}
